import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spin, Card, Typography } from 'antd';
import { useAuth } from '../contexts/AuthContext';

const { Title } = Typography;

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requiredPermissions?: string[];
  requiredRoles?: string[];
  requireAll?: boolean; // true: 需要所有权限/角色, false: 需要任一权限/角色
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  requiredPermissions = [],
  requiredRoles = [],
  requireAll = false,
}) => {
  const { state, hasPermission, hasRole, hasAnyPermission, hasAnyRole } = useAuth();
  const location = useLocation();

  // Show loading while authentication state is being determined
  if (state.loading) {
    return (
      <div style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // Check authentication requirement
  if (requireAuth && !state.authenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If authenticated but no specific permissions/roles required, allow access
  if (requiredPermissions.length === 0 && requiredRoles.length === 0) {
    return <>{children}</>;
  }

  // 管理员（admin）绕过所有检查
  if (hasRole('admin')) {
    return <>{children}</>;
  }

  // Check permissions
  let hasRequiredPermissions = true;
  if (requiredPermissions.length > 0) {
    if (requireAll) {
      hasRequiredPermissions = requiredPermissions.every(permission => hasPermission(permission));
    } else {
      hasRequiredPermissions = hasAnyPermission(requiredPermissions);
    }
  }

  // Check roles
  let hasRequiredRoles = true;
  if (requiredRoles.length > 0) {
    if (requireAll) {
      hasRequiredRoles = requiredRoles.every(role => hasRole(role));
    } else {
      hasRequiredRoles = hasAnyRole(requiredRoles);
    }
  }

  // Combine permission and role checks
  const hasAccess = hasRequiredPermissions && hasRequiredRoles;

  if (!hasAccess) {
    return (
      <Card style={{ margin: '20px', textAlign: 'center' }}>
        <div style={{ padding: '50px' }}>
          <Title level={3}>访问被拒绝</Title>
          <p>您没有权限访问此页面。</p>
          {requiredPermissions.length > 0 && (
            <p>需要权限: {requiredPermissions.join(', ')}</p>
          )}
          {requiredRoles.length > 0 && (
            <p>需要角色: {requiredRoles.join(', ')}</p>
          )}
        </div>
      </Card>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
